import { useSearchParams } from 'react-router-dom'
import { useEffect, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sourcesApi, scansApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { useDiscoveryStore } from '@/stores/discoveryStore'
import { ScanStatusBadge, StatusBadge, ModalityBadge } from '@/components/common/StatusBadge'
import { ProgressBar } from '@/components/common/ProgressBar'
import type { ScanJob, ScanStatus, Modality } from '@/api/types'

const PAGE_SIZE = 20
const STATUS_OPTIONS: ScanStatus[] = ['pending', 'running', 'completed', 'failed']

function formatDuration(started?: string, completed?: string) {
  if (!started) return '—'
  const end = completed ? new Date(completed) : new Date()
  const secs = Math.floor((end.getTime() - new Date(started).getTime()) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ${secs % 60}s`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function ScanJobs() {
  const { currentOrgId } = useOrgStore()
  const { scanFilters, setScanFilters } = useDiscoveryStore()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [pages, setPages] = useState<ScanJob[][]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Support pre-filtering via URL query param
  useEffect(() => {
    const sid = searchParams.get('source_id')
    if (sid) setScanFilters({ source_id: sid })
  }, [searchParams, setScanFilters])

  // Reset pagination when filters change
  useEffect(() => {
    setPages([])
    setOffset(0)
    setHasMore(false)
  }, [scanFilters.status, scanFilters.source_id])

  const { isLoading, isError, error, refetch, isFetching, data: scansResp } = useQuery({
    queryKey: ['scans', currentOrgId, scanFilters, offset],
    queryFn: () =>
      scansApi.list(currentOrgId, {
        status: scanFilters.status || undefined,
        source_id: scanFilters.source_id || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    refetchInterval: (query) => {
      const batch = query.state.data?.data ?? []
      return batch.some((s) => s.status === 'running' || s.status === 'pending') ? 5000 : false
    },
  })

  // Sync pages from query data — runs on cache hit AND fresh fetch,
  // so SPA navigation (cache hit, no queryFn re-run) still populates pages.
  useEffect(() => {
    if (!scansResp) return
    const batch = scansResp.data ?? []
    const pageIdx = offset / PAGE_SIZE
    setPages((prev) => {
      const next = [...prev]
      next[pageIdx] = batch
      return next
    })
    setHasMore(batch.length === PAGE_SIZE)
  }, [scansResp, offset])

  const { data: sourcesResp } = useQuery({
    queryKey: ['sources', currentOrgId],
    queryFn: () => sourcesApi.list(currentOrgId),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => scansApi.cancel(currentOrgId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scans', currentOrgId] }),
  })

  const loadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE)
  }, [])

  // Auto-load when sentinel scrolls into view
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, isFetching, loadMore])

  const scans = pages.flat()
  const sources = sourcesResp?.data ?? []
  const sourceMap = Object.fromEntries(sources.map((s) => [s.id, s.name]))
  const modalityMap = Object.fromEntries(sources.map((s) => [s.id, s.modality]))

  const runningCount = scans.filter((s) => s.status === 'running').length
  const pendingCount = scans.filter((s) => s.status === 'pending').length

  // Tick every second while any scan is running/pending to keep duration column live.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (runningCount === 0 && pendingCount === 0) return
    const timer = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(timer)
  }, [runningCount, pendingCount])

  return (
    <div className="space-y-6" id="scans-page">
      {isError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-error">Failed to load scan jobs</p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
          </div>
          <button onClick={() => refetch()} className="text-sm text-error hover:underline shrink-0">
            Retry
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Scan Jobs</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            {scans.length} scan{scans.length !== 1 ? 's' : ''} loaded
            {runningCount > 0 && (
              <span className="ml-2 text-primary dark:text-secondary">
                · {runningCount} running
              </span>
            )}
            {pendingCount > 0 && (
              <span className="ml-2 text-subtle-light dark:text-subtle-dark">
                · {pendingCount} pending
              </span>
            )}
          </p>
        </div>
        {(runningCount > 0 || pendingCount > 0) && (
          <div className="flex items-center gap-1.5 text-sm text-primary dark:text-secondary">
            <span className="material-icons text-base animate-spin">sync</span>
            Auto-refreshing
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
        <div className="flex flex-wrap gap-3">
          <select
            id="scan-status-filter"
            value={scanFilters.status}
            onChange={(e) => setScanFilters({ status: e.target.value as ScanStatus | '' })}
            className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          <select
            id="scan-source-filter"
            value={scanFilters.source_id}
            onChange={(e) => setScanFilters({ source_id: e.target.value })}
            className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All Sources</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          {(scanFilters.status || scanFilters.source_id) && (
            <button
              onClick={() => setScanFilters({ status: '', source_id: '' })}
              className="flex items-center gap-1 px-3 py-2 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
            >
              <span className="material-icons text-base">clear</span>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" id="scans-table">
            <thead className="bg-container-light dark:bg-container-dark">
              <tr>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Scan ID
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Source
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Modality
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Status
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark w-40">
                  Progress
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Trigger
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Started
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Duration
                </th>
                <th className="px-6 py-3 text-right label-sm text-subtle-light dark:text-subtle-dark">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && scans.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center">
                    <span className="material-icons animate-spin text-primary text-2xl">sync</span>
                  </td>
                </tr>
              ) : scans.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-10 text-center text-subtle-light dark:text-subtle-dark"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className="material-icons text-4xl opacity-40">manage_search</span>
                      <p>No scan jobs found.</p>
                      <Link to="/sources" className="text-primary hover:underline text-sm">
                        Go to Sources to trigger a scan
                      </Link>
                    </div>
                  </td>
                </tr>
              ) : (
                scans.map((scan) => (
                  <tr
                    key={scan.id}
                    className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                  >
                    <td className="px-6 py-3">
                      <Link
                        to={`/scans/${scan.id}`}
                        className="font-mono text-xs text-primary hover:underline"
                        id={`scan-link-${scan.id}`}
                      >
                        {scan.id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-6 py-3">
                      {sourceMap[scan.source_id] ? (
                        <Link
                          to={`/sources/${scan.source_id}`}
                          className="text-text-light dark:text-text-dark hover:text-primary text-xs"
                        >
                          {sourceMap[scan.source_id]}
                        </Link>
                      ) : (
                        <span className="font-mono text-xs text-subtle-light dark:text-subtle-dark">
                          {scan.source_id.slice(0, 8)}…
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {modalityMap[scan.source_id]
                        ? <ModalityBadge modality={modalityMap[scan.source_id] as Modality} />
                        : <span className="text-subtle-light dark:text-subtle-dark">—</span>}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-1.5">
                        <ScanStatusBadge status={scan.status} />
                        {/* A rolling job is one continuously-updated record,
                            not a scan that happened once. Without this it
                            looks like a stale completed scan that keeps
                            changing its numbers. */}
                        {scan.is_rolling && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                            title="Rolling inventory — the monitoring agent keeps updating this job instead of creating a new one"
                          >
                            rolling
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <ProgressBar
                        value={scan.progress}
                        animated={scan.status === 'running'}
                        size="sm"
                        showLabel
                      />
                    </td>
                    <td className="px-6 py-3">
                      <StatusBadge label={scan.trigger_type} variant="neutral" />
                    </td>
                    <td className="px-6 py-3 text-xs text-subtle-light dark:text-subtle-dark">
                      {formatDate(scan.started_at)}
                    </td>
                    <td className="px-6 py-3 text-xs text-subtle-light dark:text-subtle-dark">
                      {formatDuration(scan.started_at, scan.completed_at)}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/scans/${scan.id}`}
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10"
                          title="View Details"
                        >
                          <span className="material-icons text-base">open_in_new</span>
                        </Link>
                        {(scan.status === 'pending' || scan.status === 'running') && (
                          <button
                            title="Cancel"
                            onClick={() => cancelMutation.mutate(scan.id)}
                            disabled={cancelMutation.isPending}
                            className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-error hover:bg-error/10"
                          >
                            <span className="material-icons text-base">stop</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Sentinel + loading indicator */}
        <div ref={sentinelRef} className="px-6 py-3 border-t border-border-light dark:border-border-dark">
          {isFetching && scans.length > 0 ? (
            <div className="flex items-center justify-center gap-2 text-sm text-subtle-light dark:text-subtle-dark">
              <span className="material-icons text-base animate-spin">sync</span>
              Loading older scans…
            </div>
          ) : !hasMore && scans.length > 0 ? (
            <p className="text-center text-xs text-subtle-light dark:text-subtle-dark">
              All {scans.length} scans loaded
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
